import { useEffect, useState } from 'react';

/**
 * Il chiaro/scuro dell'applicazione.
 *
 * ── perché sul dispositivo e non sulla persona
 *
 * Il tema sta in D1 perché è della persona: due che usano la stessa postazione devono
 * vederne due diversi. Il chiaro/scuro no — dipende dalla luce della stanza e dall'ora, non
 * da chi sei. Chi entra dal telefono la sera e dal computer di segreteria la mattina non
 * vuole ritrovarsi il buio in ufficio con la finestra alle spalle. Per ora vive in
 * `localStorage`, che è la memoria del vetro.
 *
 * ── perché la preferenza sta qui e non nel componente dell'interruttore
 *
 * Ci stava, e da lì nasceva un difetto che si vedeva ogni volta. Lo stato partiva da
 * «chiaro» e un effetto leggeva `localStorage` **dopo il montaggio**: finché l'interruttore
 * non compariva in pagina, l'attributo sul documento non veniva scritto affatto. Sul
 * telefono l'interruttore sta nella barra laterale, che il telefono non ha, quindi si
 * entrava e l'applicazione restava chiara; poi si apriva la propria anagrafica — dove c'è
 * l'altro interruttore, dentro la scelta del tema — quello si montava, leggeva `si`, e
 * tutta l'applicazione diventava scura di colpo. Una preferenza che si accende cambiando
 * schermata non sembra una preferenza: sembra un guasto.
 *
 * La preferenza è dell'applicazione, quindi si applica all'avvio dell'applicazione —
 * `avviaBuio()` in `main.tsx`, prima che React disegni — e non quando qualcuno decide di
 * disegnare un pulsante. Chi aggiunge una schermata continua a non dover sapere che il buio
 * esiste, che era già la regola: cambia solo chi la fa rispettare.
 *
 * ── e perché lo stato è del modulo e non del componente
 *
 * Gli interruttori in pagina sono più d'uno — la barra laterale e la scelta del tema — e
 * ognuno con il proprio `useState` teneva una verità sua: premevi quello dell'anagrafica e
 * l'altro restava a mostrare la luna. Qui la verità è una, e i pulsanti la guardano.
 */

const CHIAVE = 'poligono.buio';

let acceso = false;
const inAscolto = new Set<() => void>();

function leggi(): boolean {
  try {
    return window.localStorage.getItem(CHIAVE) === 'si';
  } catch {
    // Lettura negata (navigazione privata, cookie di terze parti bloccati): si resta
    // chiari, che è il verso in cui l'applicazione è leggibile comunque.
    return false;
  }
}

/**
 * Scrive l'attributo che i fogli di stile guardano.
 *
 * `data-buio` sta accanto a `data-tema`, sullo stesso elemento e con la stessa logica: i
 * fogli guardano lì e nessun componente si porta dietro il colore.
 */
function applica(): void {
  if (acceso) document.documentElement.dataset.buio = 'si';
  else delete document.documentElement.dataset.buio;
}

/**
 * Da chiamare una volta sola, all'avvio, prima del primo disegno.
 *
 * Prima di React e non dentro un effetto: un effetto gira dopo il primo disegno, e per
 * quell'istante l'applicazione sarebbe chiara anche per chi ha scelto il buio.
 */
export function avviaBuio(): void {
  acceso = leggi();
  applica();
}

export function alternaBuio(): void {
  acceso = !acceso;
  try {
    window.localStorage.setItem(CHIAVE, acceso ? 'si' : 'no');
  } catch {
    // Scrittura negata: vale per questa visita e basta. Meglio che non rispondere al tocco.
  }
  applica();
  for (const avvisa of [...inAscolto]) avvisa();
}

/** Segue la preferenza: serve ai pulsanti, per mostrare il sole o la luna. */
export function useBuio(): boolean {
  const [, ridisegna] = useState(0);

  useEffect(() => {
    const f = () => ridisegna((n) => n + 1);
    inAscolto.add(f);
    return () => {
      inAscolto.delete(f);
    };
  }, []);

  return acceso;
}
